import { type FC } from 'react';
import { type Note } from './NotesModal';
import { FaRegEdit, FaRegTrashAlt } from 'react-icons/fa';
import { convertToReadableDate } from 'utils/dateUtils/dateUtils';

interface NoteItemProps {
  note: Note;
  noteIndex: number;
  onEdit: (note: Note) => void;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
}

export const NoteItem: FC<NoteItemProps> = ({
  note,
  noteIndex,
  onEdit,
  onDelete,
  isDeleting = false,
}) => {
  const fullName =
    [note.created_by_user?.given_name, note.created_by_user?.family_name]
      .filter(Boolean)
      .join(' ') || 'Unknown User';

  const noteLetter = note.letter_index
    ? note.letter_index.toUpperCase()
    : String.fromCharCode(65 + noteIndex);

  return (
    <div className="flex gap-2 items-start">
      <div className="min-h-[24px] min-w-[24px] rounded-full bg-white border border-gray-200 flex items-center justify-center mb-2.5">
        <span className="font-bold">{noteLetter}</span>
      </div>

      <div className="p-4 border rounded-lg w-full overflow-auto">
        <div className="flex mb-2.5 gap-2 justify-between items-center">
          <p className="text-xs text-gray-50/60 break-words">
            {convertToReadableDate(note.date, { onlyDate: true })} - {fullName}
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => {
                onEdit(note);
              }}
              className="text-gray-60"
              disabled={isDeleting}
            >
              <FaRegEdit />
            </button>
            <button
              onClick={() => {
                onDelete(note.id);
              }}
              className="text-red-500 hover:text-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaRegTrashAlt />
              )}
            </button>
          </div>
        </div>
        <p className="text-gray-60 break-words whitespace-pre-line">
          {note.description}
        </p>
      </div>
    </div>
  );
};
